import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { canRenameOrDelete } from './dimensionAccess'

// "Their tags disappear from future bookings. Past bookings keep their history." - sample names,
// max 2 spelled out, the rest folded into a translated "and N more" tail.
export const sampleNamesText = (
  names: string[],
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  if (names.length <= 2) return names.join(', ')
  return t('dimensionsPage:deleteConfirmSampleMore', { names: names.slice(0, 2).join(', '), count: names.length - 2 })
}

export type DeleteBlockedReason = 'default' | 'source' | 'children'

export interface DeletePlanEntry {
  dimension: Dimension
  reason: DeleteBlockedReason
}

export interface DeletePlan {
  deletable: Dimension[]
  blocked: DeletePlanEntry[]
}

/**
 * Orders `dimensionIds` deepest-first (children before their parents), so a sequential delete loop
 * never asks the backend to delete a node while any of its children still exist.
 */
export const sortDeepestFirst = (flatList: Dimension[], dimensionIds: string[]): string[] => {
  const byId = new Map(flatList.map(d => [d.dimensionId, d]))
  const depthCache = new Map<string, number>()

  const depthOf = (id: string, seen: Set<string>): number => {
    if (depthCache.has(id)) return depthCache.get(id)!
    if (seen.has(id)) return 0 // cycle guard — shouldn't happen, but never infinite-loop on one
    seen.add(id)
    const parentId = byId.get(id)?.parentDimensionId
    const depth = parentId && byId.has(parentId) ? depthOf(parentId, seen) + 1 : 0
    depthCache.set(id, depth)
    return depth
  }

  return [...dimensionIds].sort((a, b) => depthOf(b, new Set()) - depthOf(a, new Set()))
}

/**
 * Splits a selection into what's actually deletable right now vs. blocked, and why.
 *
 * A dimension is deletable only if it isn't the company's default dimension, passes the existing
 * source check (`canRenameOrDelete`), AND every one of its own children will ALSO actually be
 * deleted (or it has none) — the backend's
 * single-row delete doesn't check for children at all, so the only thing preventing orphaning is
 * never asking it to delete a node that still has children at that moment. This is transitive: a
 * child that's merely *selected* but itself blocked (e.g. by source) does NOT satisfy this — if it
 * did, deleting the parent while that child survives would orphan it, which is exactly the bug this
 * function exists to prevent. Children are therefore resolved before their parents (deepest-first),
 * so a block correctly propagates upward: block one child and its parent blocks too.
 */
export const computeDeletePlan = (flatList: Dimension[], dimensionIds: string[], canEdit: boolean): DeletePlan => {
  const byId = new Map(flatList.map(d => [d.dimensionId, d]))

  const childrenByParent = new Map<string, Dimension[]>()
  flatList.forEach(d => {
    if (d.parentDimensionId) {
      if (!childrenByParent.has(d.parentDimensionId)) childrenByParent.set(d.parentDimensionId, [])
      childrenByParent.get(d.parentDimensionId)!.push(d)
    }
  })

  const deletableIds = new Set<string>()
  const deletable: Dimension[] = []
  const blocked: DeletePlanEntry[] = []

  sortDeepestFirst(flatList, dimensionIds).forEach(id => {
    const dimension = byId.get(id)
    if (!dimension) return

    if (dimension.default) {
      blocked.push({ dimension, reason: 'default' })
      return
    }

    if (!canRenameOrDelete(dimension, canEdit)) {
      blocked.push({ dimension, reason: 'source' })
      return
    }

    const children = childrenByParent.get(id) || []
    const allChildrenWillBeDeleted = children.every(child => deletableIds.has(child.dimensionId))
    if (!allChildrenWillBeDeleted) {
      blocked.push({ dimension, reason: 'children' })
      return
    }

    deletableIds.add(id)
    deletable.push(dimension)
  })

  return { deletable, blocked }
}
